import { useState } from "react";
import { Button, confirm, Input, toast } from "@/components/kit";
import { formatLak } from "@/shared/lib/currency";
import {
  useCreateEventItem,
  useDeleteEventItem,
  useEventItemsQuery,
  useUpdateEventItem,
} from "../api/queries";
import { TouchField, touchControlClass } from "./touch-fields";

type EventSaleCatalogPanelProps = {
  eventId: string;
  canWrite: boolean;
};

export function EventSaleCatalogPanel({
  eventId,
  canWrite,
}: EventSaleCatalogPanelProps) {
  const items = useEventItemsQuery(eventId);
  const create = useCreateEventItem(eventId);
  const update = useUpdateEventItem(eventId);
  const remove = useDeleteEventItem(eventId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [price, setPrice] = useState(0);

  function reset() {
    setEditingId(null);
    setName("");
    setPrice(0);
  }

  async function save() {
    if (!name.trim() || price < 0) return;
    if (editingId) {
      await update.mutateAsync({
        itemId: editingId,
        name: name.trim(),
        defaultPrice: price,
      });
      toast.success("ອັບເດດລາຍການແລ້ວ");
    } else {
      await create.mutateAsync({ name: name.trim(), defaultPrice: price });
      toast.success("ເພີ່ມລາຍການແລ້ວ");
    }
    reset();
  }

  async function deleteItem(itemId: string) {
    const ok = await confirm({
      title: "ລຶບລາຍການນີ້?",
      description: "ລຶບໄດ້ຖ້າຍັງບໍ່ຖືກຂາຍ",
      actionText: "ລຶບ",
    });
    if (!ok) return;
    await remove.mutateAsync(itemId);
    if (editingId === itemId) reset();
    toast.success("ລຶບລາຍການແລ້ວ");
  }

  const rows = items.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      {canWrite ? (
        <div className="brand-card flex flex-col gap-3 p-4">
          <TouchField label="ຊື່ສິນຄ້າ">
            <Input
              className={touchControlClass}
              value={name}
              placeholder="ຊື່ສິນຄ້າ"
              onChange={(event) => setName(event.target.value)}
            />
          </TouchField>
          <TouchField label="ລາຄາເລີ່ມຕົ້ນ">
            <Input
              className={touchControlClass}
              type="number"
              min={0}
              inputMode="numeric"
              value={price}
              onChange={(event) => setPrice(Number(event.target.value))}
            />
          </TouchField>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              className="h-12 flex-1 text-base"
              disabled={!name.trim() || create.isPending || update.isPending}
              onClick={() => void save()}
            >
              {editingId ? "ບັນທຶກການແກ້" : "ເພີ່ມລ່ວງໜ້າ"}
            </Button>
            {editingId ? (
              <Button
                type="button"
                variant="outline"
                className="h-12"
                onClick={reset}
              >
                ຍົກເລີກ
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <ul className="flex flex-col gap-2">
        {rows.map((item) => (
          <li key={item.id} className="brand-card flex flex-col gap-3 p-4">
            <div className="flex items-start justify-between gap-3">
              <span className="font-medium leading-snug">{item.name}</span>
              <span className="shrink-0 tabular-nums">
                {formatLak(item.defaultPrice)}
              </span>
            </div>
            {canWrite ? (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11"
                  onClick={() => {
                    setEditingId(item.id);
                    setName(item.name);
                    setPrice(item.defaultPrice);
                  }}
                >
                  ແກ້
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11"
                  onClick={() => void deleteItem(item.id)}
                >
                  ລຶບ
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີລາຍການ</p>
      ) : null}
    </div>
  );
}
